<template>
  <div class="power-chart-container">
    <div ref="chartRef" class="chart-instance"></div>
  </div>
</template>

<script>
import * as echarts from "echarts";

export default {
  name: "PowerGrowthChart",
  props: {
    data: {
      type: Array,
      default: () => [
        { date: "07/18", price: 0 },
        { date: "07/19", price: 0 },
        { date: "07/20", price: 0 },
        { date: "07/21", price: 0 },
        { date: "07/22", price: 2 },
        { date: "07/23", price: 0 },
        { date: "07/24", price: 3 },
      ],
    },
    color: {
      type: String,
      default: "#00b4ff",
    },
  },
  data() {
    return {
      chart: null,
    };
  },
  watch: {
    data: {
      handler() {
        this.updateChart();
      },
      deep: true,
    },
  },
  mounted() {
    this.initChart();
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose();
      this.chart = null;
    }
  },
  methods: {
    initChart() {
      this.chart = echarts.init(this.$refs.chartRef);
      this.updateChart();
      window.addEventListener("resize", this.handleResize);
    },
    handleResize() {
      if (this.chart) {
        this.chart.resize();
      }
    },
    updateChart() {
      if (!this.chart) return;

      const dates = this.data.map((item) => item.date);
      const prices = this.data.map((item) => item.price || 0);

      const option = {
        grid: {
          left: "12%",
          right: "5%",
          top: "10%",
          bottom: "20%",
        },
        xAxis: {
          type: "category",
          data: dates,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: {
            color: "rgba(255, 255, 255, 0.2)",
            fontSize: 9,
            fontFamily: '"SF Mono", "Courier New", monospace',
          },
          boundaryGap: false,
        },
        yAxis: {
          type: "value",
          splitLine: {
            lineStyle: {
              color: "rgba(255, 255, 255, 0.06)",
              type: "dashed",
              width: 1,
            },
          },
          axisLabel: {
            color: "rgba(255, 255, 255, 0.25)",
            fontSize: 9,
            fontFamily: '"SF Mono", "Courier New", monospace',
            formatter: (value) => {
              if (value >= 1000) {
                return (value / 1000).toFixed(1) + "K";
              }
              return value.toFixed(0);
            },
          },
          splitNumber: 4,
          min: (value) => {
            const min = value.min;
            const max = value.max;
            if (max === 0) return -1;
            if (max < 10) return -0.5;
            if (min > 0) return min * 0.8;
            return min * 0.9;
          },
          max: (value) => {
            const max = value.max;
            if (max === 0) return 10;
            if (max < 10) return max + 2;
            return max * 1.2;
          },
        },
        series: [
          {
            type: "line",
            data: prices,
            smooth: true,
            symbol: "circle",
            symbolSize: 5,
            lineStyle: {
              width: 2,
              color: this.color,
            },
            areaStyle: {
              color: {
                type: "linear",
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: "rgba(0, 180, 255, 0.5)" },
                  { offset: 0.5, color: "rgba(0, 100, 255, 0.15)" },
                  { offset: 1, color: "rgba(0, 180, 255, 0.02)" },
                ],
              },
            },
            itemStyle: {
              color: this.color,
              borderColor: "#0a0e1a",
              borderWidth: 1.5,
            },
            emphasis: {
              focus: "series",
              itemStyle: {
                color: "#7c3aed",
                borderColor: "#0a0e1a",
                borderWidth: 2,
              },
            },
            markPoint: {
              data: [
                {
                  type: "max",
                  symbol: "circle",
                  symbolSize: 10,
                  itemStyle: {
                    color: "#7c3aed",
                    borderColor: "#0a0e1a",
                    borderWidth: 2,
                  },
                },
              ],
              label: {
                show: false,
              },
            },
          },
        ],
        tooltip: {
          trigger: "axis",
          backgroundColor: "rgba(10, 14, 26, 0.9)",
          borderColor: "rgba(255, 255, 255, 0.1)",
          borderWidth: 1,
          textStyle: {
            color: "rgba(255, 255, 255, 0.9)",
            fontSize: 12,
          },
          formatter: (params) => {
            const p = params[0];
            const currentIndex = this.data.findIndex((d) => d.date === p.name);
            const currentPrice = this.data[currentIndex]?.price || 0;

            // 计算与昨天的增幅
            let changeText = "-";
            if (currentIndex > 0) {
              const prevPrice = this.data[currentIndex - 1]?.price || 0;
              if (prevPrice === 0) {
                changeText = currentPrice > 0 ? "+100%" : "0%";
              } else {
                const change = ((currentPrice - prevPrice) / prevPrice) * 100;
                changeText = (change >= 0 ? "+" : "") + change.toFixed(1) + "%";
              }
            }

            return `
              <div style="font-weight:600;margin-bottom:4px;">${p.name}</div>
              <div>${this.$t("info.price")}: ${p.value}</div>
              <div style="font-size:11px;color:rgba(255,255,255,0.4);margin-top:4px;">
                ${this.$t("memberBox.vsYesterday")}: ${changeText}
              </div>
            `;
          },
        },
        // ❌ 删除 graphic，不显示顶部增幅
      };

      this.chart.setOption(option, true);
    },
  },
};
</script>

<style scoped>
.power-chart-container {
  /* background: rgba(255, 255, 255, 0.04); */
  border-radius: 12px;
  padding: 12px 14px;
  /* border: 1px solid rgba(255, 255, 255, 0.06); */
  transition: all 0.3s ease;
  height: 100px;
}

.power-chart-container:hover {
  border-color: rgba(0, 180, 255, 0.15);
  background: rgba(255, 255, 255, 0.06);
}

.chart-instance {
  width: 100%;
  height: 100%;
}
</style>
